/**
 * A video's length and a still frame to show for it, read on the device.
 *
 * The server never decodes video (no ffmpeg, 512 MB of memory), so the poster
 * frame that becomes the video's thumbnail is taken here, from the file the
 * technician just recorded or picked.
 */
const POSTER_MAX = 1280;
const POSTER_QUALITY = 0.8;
const TIMEOUT_MS = 15000;

/**
 * Resolves { posterBlob, posterDataUrl, durationMs } — any of them null when the
 * browser can't decode this file (e.g. HEVC in desktop Chrome). Never rejects:
 * a video without a poster still uploads.
 */
export function readVideo(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    let settled = false;
    let durationMs = null;

    const finish = (result) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      video.removeAttribute('src');
      video.load();
      URL.revokeObjectURL(url);
      resolve({ posterBlob: null, posterDataUrl: null, durationMs, ...result });
    };
    const timer = setTimeout(() => finish({}), TIMEOUT_MS);

    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.onerror = () => finish({});
    video.onloadedmetadata = () => {
      if (Number.isFinite(video.duration) && video.duration > 0) {
        durationMs = Math.round(video.duration * 1000);
      }
      // A frame a little way in: the very first one is often black.
      const target = Number.isFinite(video.duration) ? Math.min(0.5, video.duration / 2) : 0.1;
      try { video.currentTime = target; } catch { finish({}); }
    };
    video.onseeked = () => {
      const w = video.videoWidth;
      const h = video.videoHeight;
      if (!w || !h) { finish({}); return; }
      const scale = Math.min(1, POSTER_MAX / Math.max(w, h));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(w * scale);
      canvas.height = Math.round(h * scale);
      try {
        canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
      } catch { finish({}); return; }
      const posterDataUrl = canvas.toDataURL('image/jpeg', POSTER_QUALITY);
      canvas.toBlob(
        (blob) => finish({ posterBlob: blob, posterDataUrl }),
        'image/jpeg',
        POSTER_QUALITY
      );
    };
    video.src = url;
  });
}

/** 75000 -> "1:15" */
export function formatDuration(ms) {
  if (!ms && ms !== 0) return '';
  const total = Math.round(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = String(total % 60).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${m}:${s}`;
}

export const isVideoFile = (file) => !!file && (file.type || '').startsWith('video/');
